import { createContext, useContext, useState, useCallback, useMemo, useEffect } from "react";
import { requestNotificationPermission, triggerTaskReminder } from "../utils/notifications";
import { getLocalDateString, getLocalTimeString } from "../utils/dateUtils";
import { useAuth } from "./AuthContext";
import { taskService } from "../services/taskService";

const TaskContext = createContext();

export const useTasks = () => {
    const context = useContext(TaskContext);
    if (!context)
        throw new Error("useTasks debe estar dentro de un TaskContextProvider");
    return context;
};

export const TaskContextProvider = ({ children }) => {
    const { user } = useAuth();
    const [tasks, setTasks] = useState([]);
    const [adding, setAdding] = useState(false);
    const [loading, setLoading] = useState(false);

    const [triggeredReminders, setTriggeredReminders] = useState([]);

    // 1. getTasks: Estabilizada con useCallback
    const getTasks = useCallback(async () => {
        if (!user) {
            setTasks([]);
            return;
        }

        setLoading(true);
        try {
            const data = await taskService.getTasks(user.id);
            setTasks(data);
        } catch (error) {
            console.error("Error al obtener tareas:", error);
            setTasks([]);
        } finally {
            setLoading(false);
        }
    }, [user]);

    // 2. creatTasks: Modificada para recibir el payload completo del formulario
    const creatTasks = useCallback(async (taskPayload) => {
        if (!user) throw new Error("No hay usuario autenticado.");
        setAdding(true);
        try {
            const data = await taskService.createTask(taskPayload, user.id);
            setTasks((prevTasks) => [...data, ...prevTasks]); 
        } catch (error) {
            console.error("Error al crear tarea:", error);
            throw error;
        } finally {
            setAdding(false);
        }
    }, [user]);

    // 3. deleteTask: Estabilizada con useCallback
    const deleteTask = useCallback(async (id) => {
        if (!user) return;
        try {
            await taskService.deleteTask(id, user.id);
            setTasks((prevTasks) => prevTasks.filter((task) => task.id !== id));
        } catch (error) {
            console.error("Error al eliminar tarea:", error);
        }
    }, [user]);

    // 4. updateTask: Estabilizada con useCallback
    const updateTask = useCallback(async (id, updateFields) => {
        if (!user) return;
        try {
            const updated = await taskService.updateTask(id, updateFields, user.id);
            if (updated) {
                setTasks((prevTasks) =>
                    prevTasks.map(
                        (task) => (task.id === id ? updated : task)
                    )
                );
            }
        } catch (error) {
            console.error("Error al actualizar tarea:", error);
        }
    }, [user]);

    // 5. MOTOR EN TIEMPO REAL DE RECORDATORIOS
    useEffect(() => {
        // Solicitar permiso al cargar
        requestNotificationPermission();

        const checkReminders = () => {
            const now = new Date();
            
            // Obtener la fecha "YYYY-MM-DD" y hora "HH:MM" de tu reloj local
            const currentDateStr = getLocalDateString(now); 
            const currentTimeStr = getLocalTimeString(now); // Ej: "16:40"

            tasks.forEach((task) => {
                // Ignorar si la tarea está lista o ya fue notificada en esta sesión
                if (task.done || triggeredReminders.includes(task.id)) return;

                let shouldTrigger = false;

                // Cortamos a 5 caracteres para asegurar formato HH:MM (ignora los segundos :00 de Supabase)
                const taskTimeFormatted = task.due_time ? task.due_time.slice(0, 5) : "";

                // 1. Recordatorio "A la hora de la tarea"
                if (task.reminder_type === "at_time" && task.due_date && taskTimeFormatted) {
                    if (task.due_date === currentDateStr && taskTimeFormatted === currentTimeStr) {
                        shouldTrigger = true;
                    }
                }

                // 2. Recordatorio "Personalizado"
                if (task.reminder_type === "custom" && task.reminder_date) {
                    const [remDate, remTime] = task.reminder_date.replace(" ", "T").split("T");
                    const remTimeFormatted = remTime ? remTime.slice(0, 5) : "";

                    if (remDate === currentDateStr && remTimeFormatted === currentTimeStr) {
                        shouldTrigger = true;
                    }
                }

                // Disparar la alerta si coincide
                if (shouldTrigger) {
                    triggerTaskReminder(task);
                    setTriggeredReminders((prev) => [...prev, task.id]);
                }
            });
        };

        checkReminders();
        const intervalId = setInterval(checkReminders, 10000); // Revisa cada 10 segundos

        return () => clearInterval(intervalId);
    }, [tasks, triggeredReminders]);

    // Escuchar cuando finalice la inicialización de datos de ejemplo (seeding)
    useEffect(() => {
        const handleAppSeeded = () => {
            getTasks();
        };
        window.addEventListener("app_seeded", handleAppSeeded);
        return () => window.removeEventListener("app_seeded", handleAppSeeded);
    }, [getTasks]);
    

    // 6. deleteProjectTasks: Limpiar del estado local las tareas del proyecto eliminado
    const deleteProjectTasks = useCallback((projectId) => {
        setTasks((prevTasks) => prevTasks.filter((task) => task.project_id !== projectId));
    }, []);

    // 7. deleteMultipleTasks: Eliminar varias tareas en Supabase y actualizar el estado local
    const deleteMultipleTasks = useCallback(async (taskIds) => {
        if (!user || !taskIds || taskIds.length === 0) return;
        try {
            await taskService.deleteMultipleTasks(taskIds, user.id);
            const idSet = new Set(taskIds);
            setTasks((prevTasks) => prevTasks.filter((task) => !idSet.has(task.id)));
        } catch (error) {
            console.error("Error al eliminar múltiples tareas:", error);
            throw error;
        }
    }, [user]);

    // Memoizar el valor del contexto
    const contextValue = useMemo(() => ({
        tasks,
        getTasks,
        creatTasks,
        adding,
        loading,
        deleteTask,
        updateTask,
        deleteProjectTasks,
        deleteMultipleTasks,
    }), [tasks, getTasks, creatTasks, adding, loading, deleteTask, updateTask, deleteProjectTasks, deleteMultipleTasks]);

    return (
        <TaskContext.Provider value={contextValue}>
            {children}
        </TaskContext.Provider>
    );
};